/**
 * Add Pipe fitting modal
 *
 * @date 2023/12/20
 *
 * Changelog:
 * @2026/07/25 - Extract buildFeatureEntity as pure module-level function;
 *               replace useCallback+immediate-call with useMemo for ordered fields;
 *               remove pipeFields from useEffect deps by deriving fields from
 *               selectType inline — eliminates the reference-equality render loop.
 */
import * as _ from 'lodash-es';
import { useState, useEffect, useMemo } from 'react';

import { kebabCase, moveElementToBack } from '@/utils';

import { InputRendererProps } from '../inputs';
import { placeD2AfterD1 } from '../state/orders';
import { iconFinder, findFieldsByType } from '../store';
import {
  GenericObject,
  NIDSymbolEntity,
  TKEvent,
  CATALOGUE_CATEGORY as FT,
  CATALOGUE_TYPE,
  LENGTH_FIELD,
  LENGTH_STR_FIELD,
  FIELD_Z0,
  OMIT_FROM_SHOW,
} from '../types';
import {
  getDimensionBy,
  setDimensionBy,
  getSystemBy,
} from '../types/dimension-setting';

const buildFeatureEntity = (
  type: string,
  system: string,
  fieldProps: GenericObject,
): NIDSymbolEntity => ({
  category: FT.P,
  system,
  entityType: type,
  itemType: CATALOGUE_TYPE.F,
  icon: iconFinder(type),
  toolType: FT.P,
  ...fieldProps,
});

/**
 * Add Pipe Fitting
 * @param selectType
 * @param lazyCloseHandler
 * @returns
 */
export const usePipeModal = (
  selectType: string,
  lazyCloseHandler: (lastType?: string) => void,
) => {
  const lastSystem = getSystemBy(selectType) || '';
  // system belonged
  const [currentSystem, setCurrentSystem] = useState<string>(lastSystem);
  const [currentType, setCurrentType] = useState(selectType); // element type
  // shape is complex to decide, so move to an effect to check!
  // kebab case field pairs
  const [fieldProps, setFieldProps] = useState<{ [key: string]: string }>({});
  const pipeFields = useMemo(
    () =>
      findFieldsByType(currentType).filter((f) => !OMIT_FROM_SHOW.includes(f)),
    [currentType],
  );
  /**
   * figure out `fieldProps` from last used symbol setting
   */
  useEffect(() => {
    if (!selectType) return;
    // use cached dimension values or symbol detail
    const fields = findFieldsByType(selectType).filter(
      (f) => !OMIT_FROM_SHOW.includes(f),
    );
    const values = getDimensionBy(selectType);
    setFieldProps(_.pick(values, fields) as GenericObject);

    // reset system property
    const { system } = values;
    if (!system) return;
    const strSystem = system as string;
    // ensure system value contains '/' separator for correct option display
    const withSlash = strSystem.includes('/')
      ? strSystem
      : [...strSystem].join('/');
    setCurrentSystem(withSlash);
  }, [selectType]);

  /**
   * TODO: anyway to separate this function out of this hook? - 2023/07/13
   *
   * figure out the properties needed for each field input component
   *
   * @param fieldName each field name
   * @returns normal object
   */
  const inputFieldRenderer = (fieldName: string): InputRendererProps => {
    const kebabField = kebabCase(fieldName);
    const fieldValueChangeHandler = (key: string, value: string) => {
      // console.log({ key });
      const changedProps = { ...fieldProps, [key]: value };
      setFieldProps(changedProps);
    };
    const isLengthField = fieldName === LENGTH_FIELD;
    const isLengthInString = fieldName === LENGTH_STR_FIELD;
    const inputProps: InputRendererProps = {
      ...fieldProps,
      disabled: isLengthInString,
      fieldName, // `DefaultInput` need this
      fieldLabel: isLengthField ? 'LENGTH(in pixels)' : '',
      fieldValue: fieldProps[kebabField], // `DefaultInput` need this
      fieldHandler: fieldValueChangeHandler,
      blurHandler: () => null, // FIXME: placeholder for now - 2023/11/08
    };

    return inputProps;
  };

  const applyNewElementHandler = () => {
    const detail = buildFeatureEntity(currentType, currentSystem, fieldProps);
    document.dispatchEvent(new CustomEvent(TKEvent.NEWELEMENT, { detail }));
    selectType && setDimensionBy(selectType, fieldProps);
    lazyCloseHandler(currentType);
  };

  // lazy close, and dispatch cancel tool event...
  const cancelNewElementHandler = () => {
    lazyCloseHandler();
    const evt = new Event(TKEvent.CANCEL_DRAWING_TOOL);
    document.dispatchEvent(evt);
  };

  const orderedPipeFields = useMemo(() => {
    const zIndex = pipeFields.indexOf(FIELD_Z0);
    let ordered = pipeFields.filter((f) => f !== LENGTH_STR_FIELD);
    if (zIndex > -1) ordered = moveElementToBack(pipeFields, zIndex);
    return placeD2AfterD1(ordered);
  }, [pipeFields]);

  return {
    currentType,
    currentSystem,
    fieldProps,
    pipeFields: orderedPipeFields,
    inputFieldRenderer,
    setCurrentType,
    setCurrentSystem,
    applyNewElementHandler,
    cancelNewElementHandler,
  };
};
